import ModalCompanyImageGarrely from 'components/modal/ModalCompanyImageGarrely';
import { AppHelper } from 'helpers/appHelper';
import ImageResize from 'quill-image-resize-module-react';
import { useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';

import 'react-quill/dist/quill.snow.css';
import QuillModalButton from './parts/QuillModalButton';

// 画像リサイズモジュールの登録
Quill.register('modules/imageResize', ImageResize);

// カスタムボタンの定義
const Embed = Quill.import('blots/embed');

class CustomLinkBlot extends Embed {
  static create(value) {
    const node = document.createElement('span');
    node.setAttribute('class', 'ql-button');
    const link = document.createElement('a');
    link.setAttribute('href', value.url);
    link.setAttribute('target', '_blank');
    link.textContent = value.text;
    node.appendChild(link);
    return node;
  }

  static value(node) {
    // <a>タグを安全に取得するためのチェックを追加
    const link = node.querySelector('a');
    if (link) {
      return {
        url: link.getAttribute('href'),
        text: link.textContent,
      };
    }
    // <a>タグが見つからない場合は、デフォルト値またはエラーを処理する
    return { url: '', text: '' };
  }
}
CustomLinkBlot.blotName = 'customLink';
CustomLinkBlot.tagName = 'span';
CustomLinkBlot.className = 'ql-button';
Quill.register(CustomLinkBlot);

/**
 * Quillエディタコンポーネント
 */
export default function QuillEditor(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  toolbar?: ('link' | 'button' | 'image')[];
  readOnly?: boolean;
  className?: string;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [isImageModalOpenState, setIsImageModalOpenState] = useState(false);
  const [isButtonModalOpenState, setIsButtonModalOpenState] = useState(false);
  // Quillエディタの参照
  const editorRef = useRef(null);
  // ツールバーに割り当てるUID
  const uuid = AppHelper.getUid(14);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　カンパニー画像選択モーダルを開きます。
   */
  const openCompanyImageModal = () => {
    setIsImageModalOpenState(true);
  };

  /**
   *　カスタムボタン設定モーダルを開きます。
   */
  const openButtonModal = () => {
    setIsButtonModalOpenState(true);
  };

  /**
   *　カンパニー画像を挿入します。
   */
  const onInsertCompanyImage = async (imageKey: string) => {
    if (imageKey) {
      const url = AppHelper.getCompanyImageUrl(imageKey);
      const range = editorRef.current.editor.getSelection();

      editorRef.current.editor.insertEmbed(range?.index || 0, 'image', url);
    }
  };

  /**
   *　カスタムボタンを挿入します。
   */
  const onInsertButton = async (buttonText: string, buttonUrl: string) => {
    const text = buttonText;
    const url = buttonUrl;
    if (buttonUrl && buttonText) {
      const editor = editorRef.current.editor;
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, 'customLink', { url, text }, 'user');
      editor.setSelection(range.index + 1, 'silent');
    }
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */

  // Quillエディタのモジュール設定
  const modules = useMemo(
    () => ({
      toolbar: {
        container: '#toolbar-' + uuid,
        handlers: {
          companyImage: openCompanyImageModal,
          linkButton: openButtonModal,
        },
      },
      clipboard: {
        matchVisual: false,
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
      },
    }),
    []
  );

  return (
    <>
      <div className={`quill-editor ${props.className || ''}`}>
        <div id={`toolbar-${uuid}`}>
          <span className="ql-formats">
            <button className="ql-bold" />
            {props.toolbar?.includes('link') && <button className="ql-link" />}
            {props.toolbar?.includes('image') && (
              <button className="ql-companyImage" type="button">
                <svg viewBox="0 0 18 18">
                  <rect className="ql-stroke" height="10" width="12" x="3" y="4"></rect>
                  <circle className="ql-fill" cx="6" cy="7" r="1"></circle>{' '}
                  <polyline
                    className="ql-even ql-fill"
                    points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"
                  ></polyline>
                </svg>
              </button>
            )}
            {props.toolbar?.includes('button') && (
              <button className="ql-linkButton" type="button">
                <svg width="18" height="18" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" fill="none" width="20" height="20" />

                  <g>
                    <path d="M17 5H3c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm1 7c0 .6-.4 1-1 1H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h14c.6 0 1 .4 1 1v5z" />
                  </g>
                </svg>
              </button>
            )}
          </span>
          {/* <span className="ql-formats">
            <select className="ql-font" defaultValue="arial">
              <option value="arial">Arial</option>
              <option value="comic-sans">Comic Sans</option>
              <option value="courier-new">Courier New</option>
              <option value="georgia">Georgia</option>
              <option value="helvetica">Helvetica</option>
              <option value="lucida">Lucida</option>
            </select>
            <select className="ql-size" defaultValue="medium">
              <option value="extra-small">Size 1</option>
              <option value="small">Size 2</option>
              <option value="medium">Size 3</option>
              <option value="large">Size 4</option>
            </select>
            <select className="ql-header" defaultValue="3">
              <option value="1">Heading</option>
              <option value="2">Subheading</option>
              <option value="3">Normal</option>
            </select>
          </span>
          <span className="ql-formats">
            <button className="ql-bold" />
            <button className="ql-italic" />
            <button className="ql-underline" />
            <button className="ql-strike" />
          </span>
          <span className="ql-formats">
            <button className="ql-list" value="ordered" />
            <button className="ql-list" value="bullet" />
            <button className="ql-indent" value="-1" />
            <button className="ql-indent" value="+1" />
          </span>
          <span className="ql-formats">
            <button className="ql-script" value="super" />
            <button className="ql-script" value="sub" />
            <button className="ql-blockquote" />
            <button className="ql-direction" />
          </span>
          <span className="ql-formats">
            <select className="ql-align" />
            <select className="ql-color" />
            <select className="ql-background" />
          </span>
          <span className="ql-formats">
            <button className="ql-link" />
            <button className="ql-video" />
            <button className="ql-image" />
            <button className="ql-video" />
          </span>
          <span className="ql-formats">
            <button className="ql-formula" />
            <button className="ql-code-block" />
            <button className="ql-clean" />
          </span> */}
        </div>
        <ReactQuill
          ref={editorRef}
          modules={modules}
          // formats={formats}
          theme="snow"
          value={props.value}
          onChange={props.onChange}
          placeholder={props.placeholder}
          readOnly={props.readOnly}
        />
      </div>
      <ModalCompanyImageGarrely
        setOpen={setIsImageModalOpenState}
        open={isImageModalOpenState}
        onImageSelected={onInsertCompanyImage}
      />
      <QuillModalButton
        setOpen={setIsButtonModalOpenState}
        open={isButtonModalOpenState}
        onSubmit={onInsertButton}
      />
    </>
  );
}
